import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getContactActivity, createAdminOrder, type CreateOrderInput } from "@/services/adminService";
import { useAdminCounters } from "@/pages/portal/admin/context/AdminCountersContext";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import { adminInput, adminLabel, adminBtnPrimary, adminBtnGhost } from "@/pages/portal/admin/config/ui";
import type { CrmContact, CrmStage, ErpOrder, ErpInvoice, ErpOrderStatus } from "@/types/admin";

interface ContactDrawerProps {
  contact: CrmContact;
  onClose: () => void;
  onEdit: (contact: CrmContact) => void;
}

const STAGE_BADGE: Record<CrmStage, string> = {
  new: "bg-secondary-100 text-secondary-800",
  contacted: "bg-accent-100 text-accent-800",
  qualified: "bg-primary-100 text-primary-700",
  proposal: "bg-amber-100 text-amber-800",
  won: "bg-emerald-100 text-emerald-800",
  lost: "bg-rose-100 text-rose-700",
};

const ORDER_BADGE: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-secondary-100 text-secondary-800",
  processing: "bg-primary-100 text-primary-700",
  fulfilled: "bg-emerald-100 text-emerald-800",
  refunded: "bg-rose-100 text-rose-700",
  cancelled: "bg-background-200 text-foreground-600",
  draft: "bg-background-200 text-foreground-600",
  sent: "bg-secondary-100 text-secondary-800",
  overdue: "bg-rose-100 text-rose-700",
  void: "bg-background-200 text-foreground-600",
};

const PAYMENT_METHODS = ["Manual entry", "Bank transfer", "UPI", "Cash", "Card", "Cheque"];
const CREATE_STATUSES: ErpOrderStatus[] = ["pending", "paid", "processing", "fulfilled"];

const inr = (n: number) => `₹${n.toLocaleString("en-IN")}`;
const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

interface TimelineEvent {
  icon: string;
  tone: string;
  title: string;
  meta: string;
  date: string;
}

type Notice = { kind: "success" | "error"; text: string } | null;

/** Right-side drawer showing one contact's profile, spend and full activity. */
export default function ContactDrawer({ contact, onClose, onEdit }: ContactDrawerProps) {
  const { accessToken } = useAuth();
  const { refresh: refreshCounters } = useAdminCounters();
  const [orders, setOrders] = useState<ErpOrder[]>([]);
  const [invoices, setInvoices] = useState<ErpInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState<Notice>(null);

  // quick "create order for this contact" form
  const [orderForm, setOrderForm] = useState<CreateOrderInput | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderError, setOrderError] = useState("");

  const loadActivity = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getContactActivity(accessToken, contact);
      setOrders(res.orders);
      setInvoices(res.invoices);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this contact's activity.");
    } finally {
      setLoading(false);
    }
  }, [accessToken, contact]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const totals = useMemo(() => {
    const paid = orders
      .filter((o) => o.status === "paid" || o.status === "fulfilled" || o.status === "processing")
      .reduce((s, o) => s + o.amount, 0);
    const invoiced = invoices.reduce((s, i) => s + i.total, 0);
    const outstanding = invoices
      .filter((i) => i.status === "sent" || i.status === "overdue")
      .reduce((s, i) => s + i.total, 0);
    return { paid, invoiced, outstanding };
  }, [orders, invoices]);

  const timeline = useMemo<TimelineEvent[]>(() => {
    const events: TimelineEvent[] = [
      {
        icon: "ri-user-add-line",
        tone: "bg-primary-100 text-primary-700",
        title: "Contact added to CRM",
        meta: `Source: ${contact.source}`,
        date: contact.createdAt,
      },
    ];
    orders.forEach((o) =>
      events.push({
        icon: "ri-shopping-bag-3-line",
        tone: "bg-secondary-100 text-secondary-700",
        title: `Order ${o.orderNo} · ${o.plan || "Listing plan"}`,
        meta: `${inr(o.amount)} · ${o.status}`,
        date: o.createdAt,
      }),
    );
    invoices.forEach((i) =>
      events.push({
        icon: "ri-file-list-3-line",
        tone: "bg-accent-100 text-accent-800",
        title: `Invoice ${i.invoiceNo} · ${i.plan || "Listing plan"}`,
        meta: `${inr(i.total)} · ${i.status}`,
        date: i.issuedAt,
      }),
    );
    return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [contact, orders, invoices]);

  function openOrder() {
    setOrderError("");
    setOrderForm({
      customer: contact.name,
      customerEmail: contact.email,
      business: contact.company,
      plan: "",
      amount: 0,
      currency: "INR",
      paymentMethod: "Manual entry",
      status: "pending",
    });
  }

  const setOrder = <K extends keyof CreateOrderInput>(key: K, value: CreateOrderInput[K]) =>
    setOrderForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  async function submitOrder() {
    if (!orderForm?.customer?.trim()) return;
    setSavingOrder(true);
    setOrderError("");
    try {
      const created = await createAdminOrder(accessToken, orderForm);
      setOrderForm(null);
      setNotice({ kind: "success", text: `Order ${created.orderNo} created for ${contact.name}.` });
      await loadActivity();
      refreshCounters();
    } catch (err) {
      setOrderError(err instanceof Error ? err.message : "Could not create the order.");
    } finally {
      setSavingOrder(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex justify-end bg-foreground-950/50" onClick={onClose}>
        <div
          className="flex h-full w-full max-w-md flex-col overflow-hidden border-l border-background-200 bg-background-50"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="border-b border-background-200 px-6 py-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 font-heading text-base font-bold text-background-50">
                  {contact.name.slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <h2 className="font-heading text-base font-bold text-foreground-950">{contact.name}</h2>
                  <p className="text-xs text-foreground-500">{contact.company || "—"}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
                aria-label="Close drawer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${STAGE_BADGE[contact.stage]}`}>
                {contact.stage}
              </span>
              <button
                type="button"
                onClick={openOrder}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1 text-[11px] font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-add-line text-sm" /> Create order
              </button>
              <button
                type="button"
                onClick={() => onEdit(contact)}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1 text-[11px] font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-pencil-line text-sm" /> Edit contact
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2.5 text-xs text-rose-700">
                <i className="ri-error-warning-line mt-0.5 text-base" /> {error}
              </div>
            )}

            {notice && (
              <div
                className={`mb-4 flex items-start justify-between gap-2 rounded-lg px-3 py-2.5 text-xs ${
                  notice.kind === "success" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                }`}
              >
                <span className="flex items-start gap-2">
                  <i className={`${notice.kind === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} mt-0.5 text-base`} />
                  {notice.text}
                </span>
                <button type="button" onClick={() => setNotice(null)} className="cursor-pointer text-current opacity-70 hover:opacity-100">
                  <i className="ri-close-line text-base" />
                </button>
              </div>
            )}

            {/* Contact details */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Email", value: contact.email || "—", icon: "ri-mail-line" },
                { label: "Phone", value: contact.phone || "—", icon: "ri-phone-line" },
                { label: "Owner", value: contact.owner, icon: "ri-user-star-line" },
                { label: "Deal value", value: inr(contact.value), icon: "ri-money-rupee-circle-line" },
              ].map((f) => (
                <div key={f.label} className="rounded-lg border border-background-200 bg-background-100/50 px-3 py-2.5">
                  <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-foreground-500">
                    <i className={`${f.icon} text-sm`} /> {f.label}
                  </p>
                  <p className="mt-0.5 truncate text-xs font-medium text-foreground-800">{f.value}</p>
                </div>
              ))}
            </div>

            {/* Spend summary */}
            <div className="mt-5 grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-emerald-50 px-3 py-2.5 text-center">
                <p className="font-heading text-base font-bold text-emerald-700">{loading ? "—" : inr(totals.paid)}</p>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600">Paid</p>
              </div>
              <div className="rounded-lg bg-primary-50 px-3 py-2.5 text-center">
                <p className="font-heading text-base font-bold text-primary-700">{loading ? "—" : inr(totals.invoiced)}</p>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-600">Invoiced</p>
              </div>
              <div className="rounded-lg bg-amber-50 px-3 py-2.5 text-center">
                <p className="font-heading text-base font-bold text-amber-700">{loading ? "—" : inr(totals.outstanding)}</p>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-600">Due</p>
              </div>
            </div>

            {/* Orders */}
            <div className="mt-6">
              <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground-600">
                <i className="ri-shopping-bag-3-line text-sm" /> Orders
                <span className="rounded-full bg-background-100 px-2 py-0.5 text-[10px] text-foreground-600">{orders.length}</span>
              </h3>
              {loading ? (
                <p className="py-3 text-center text-xs text-foreground-400"><i className="ri-loader-4-line animate-spin text-lg" /></p>
              ) : orders.length === 0 ? (
                <div className="rounded-lg border border-dashed border-background-300 px-3 py-4 text-center">
                  <p className="text-xs text-foreground-500">No orders linked to this contact.</p>
                  <button
                    type="button"
                    onClick={openOrder}
                    className="mt-2 inline-flex cursor-pointer items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700"
                  >
                    <i className="ri-add-line text-sm" /> Create the first order
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {orders.map((o) => (
                    <div key={o.id} className="flex items-center justify-between rounded-lg border border-background-200 px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="truncate text-xs font-semibold text-foreground-900">{o.orderNo}</p>
                        <p className="truncate text-[11px] text-foreground-500">{o.plan} · {fmtDate(o.createdAt)}</p>
                      </div>
                      <div className="ml-2 shrink-0 text-right">
                        <p className="text-xs font-semibold tabular-nums text-foreground-900">{inr(o.amount)}</p>
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${ORDER_BADGE[o.status] ?? "bg-background-200 text-foreground-600"}`}>
                          {o.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Invoices */}
            <div className="mt-6">
              <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground-600">
                <i className="ri-file-list-3-line text-sm" /> Invoices
                <span className="rounded-full bg-background-100 px-2 py-0.5 text-[10px] text-foreground-600">{invoices.length}</span>
              </h3>
              {loading ? (
                <p className="py-3 text-center text-xs text-foreground-400"><i className="ri-loader-4-line animate-spin text-lg" /></p>
              ) : invoices.length === 0 ? (
                <p className="rounded-lg border border-dashed border-background-300 px-3 py-3 text-center text-xs text-foreground-500">
                  No invoices for this contact.
                </p>
              ) : (
                <div className="space-y-2">
                  {invoices.map((i) => (
                    <div key={i.id} className="flex items-center justify-between rounded-lg border border-background-200 px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="truncate text-xs font-semibold text-foreground-900">{i.invoiceNo}</p>
                        <p className="truncate text-[11px] text-foreground-500">{i.plan} · due {fmtDate(i.dueAt)}</p>
                      </div>
                      <div className="ml-2 shrink-0 text-right">
                        <p className="text-xs font-semibold tabular-nums text-foreground-900">{inr(i.total)}</p>
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${ORDER_BADGE[i.status] ?? "bg-background-200 text-foreground-600"}`}>
                          {i.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Activity timeline */}
            <div className="mt-6">
              <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground-600">
                <i className="ri-history-line text-sm" /> Activity
              </h3>
              <ol className="relative space-y-3.5 border-l border-background-200 pl-5">
                {timeline.map((ev, idx) => (
                  <li key={idx} className="relative">
                    <span className={`absolute -left-[27px] flex h-6 w-6 items-center justify-center rounded-full ${ev.tone}`}>
                      <i className={`${ev.icon} text-xs`} />
                    </span>
                    <p className="text-xs font-semibold text-foreground-800">{ev.title}</p>
                    <p className="text-[11px] text-foreground-500">{ev.meta} · {fmtDate(ev.date)}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>

      {orderForm !== null && (
        <AdminModal
          onClose={() => { setOrderForm(null); setOrderError(""); }}
          title="Create order for contact"
          subtitle={`Raise a listing-plan order for ${contact.name}.`}
          footer={
            <>
              <button type="button" onClick={() => { setOrderForm(null); setOrderError(""); }} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={!orderForm?.customer?.trim() || savingOrder} onClick={submitOrder} className={adminBtnPrimary}>
                {savingOrder ? "Creating…" : "Create order"}
              </button>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={adminLabel} htmlFor="cd-customer">Customer</label>
              <input id="cd-customer" type="text" value={orderForm.customer} onChange={(e) => setOrder("customer", e.target.value)} className={adminInput} />
            </div>
            <div className="sm:col-span-2">
              <label className={adminLabel} htmlFor="cd-email">Customer email</label>
              <input id="cd-email" type="email" value={orderForm.customerEmail ?? ""} onChange={(e) => setOrder("customerEmail", e.target.value)} className={adminInput} placeholder="optional — links to their portal orders" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="cd-business">Business</label>
              <input id="cd-business" type="text" value={orderForm.business ?? ""} onChange={(e) => setOrder("business", e.target.value)} className={adminInput} placeholder="e.g. Sunrise Bakery" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="cd-plan">Plan</label>
              <input id="cd-plan" type="text" value={orderForm.plan ?? ""} onChange={(e) => setOrder("plan", e.target.value)} className={adminInput} placeholder="e.g. Premium Yearly" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="cd-amount">Amount (₹)</label>
              <input id="cd-amount" type="number" min={0} value={orderForm.amount ?? 0} onChange={(e) => setOrder("amount", Number(e.target.value) || 0)} className={adminInput} />
            </div>
            <div>
              <label className={adminLabel} htmlFor="cd-payment">Payment method</label>
              <select id="cd-payment" value={orderForm.paymentMethod ?? "Manual entry"} onChange={(e) => setOrder("paymentMethod", e.target.value)} className={`${adminInput} cursor-pointer`}>
                {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className={adminLabel} htmlFor="cd-status">Status</label>
              <select id="cd-status" value={orderForm.status ?? "pending"} onChange={(e) => setOrder("status", e.target.value as ErpOrderStatus)} className={`${adminInput} cursor-pointer`}>
                {CREATE_STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </div>
          </div>
          {orderError && (
            <p className="mt-4 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              <i className="ri-error-warning-line text-base" /> {orderError}
            </p>
          )}
        </AdminModal>
      )}
    </>
  );
}